// src/components/EditCar.tsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import Gallery from "./Gallery";
import { uploadToCloudinary } from "../utils/cloudinary";
import "../CSS/App.css";
import axios from "axios";

interface Car {
  id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
  price: number;
  model: string;
  year: number;
  mileage: number;
  country: "Lebanon" | "Abidjan";
  createdAt: number;
  showInFlipcard: boolean;
}

const EditCar: React.FC = () => {
  const [formData, setFormData] = useState({
    title: "",
    text: "",
    price: 0,
    model: "",
    year: new Date().getFullYear(),
    mileage: 0,
    country: "Lebanon" as "Lebanon" | "Abidjan",
  });
  const [imageUrl, setImageUrl] = useState<string>("");
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [showInFlipcard, setShowInFlipcard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchCar = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await axios.get(
          process.env.REACT_APP_API + `/cars/${id}`
        );
        const data = response.data as Car;
        setFormData({
          title: data.title,
          text: data.text,
          price: data.price,
          model: data.model,
          year: data.year,
          mileage: data.mileage,
          country: data.country,
        });
        setImageUrl(data.imageUrl || "");
        setGalleryImages(data.galleryImages || []);
        setYoutubeUrl(data.youtubeUrl || "");
        setShowInFlipcard(data.showInFlipcard || false);
      } catch (error) {
        console.error("Error fetching car:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch car details",
          style: { zIndex: 9999 },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id]);

  const handleMainImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const uploadedUrl = await uploadToCloudinary(file);
      setImageUrl(uploadedUrl);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Main image uploaded successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to upload image",
        life: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files) return;

    try {
      setUploading(true);
      const uploadPromises = Array.from(files).map((file) =>
        uploadToCloudinary(file)
      );
      const uploadedUrls = await Promise.all(uploadPromises);
      setGalleryImages((prev) => [...prev, ...uploadedUrls]);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Gallery images uploaded successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error uploading gallery images:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to upload gallery images",
        life: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setLoading(true);

      // If enabling flipcard, check the limit
      if (showInFlipcard) {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/cars?showInFlipcard=true`
        );
        const currentFlipCards = response.data.filter(
          (car: any) => car.id !== id
        );

        if (currentFlipCards.length >= 5) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              "Maximum limit of 5 flipcards reached. Please uncheck some posts first.",
            life: 5000,
          });
          setShowInFlipcard(false);
          return;
        }
      }

      await axios.put(process.env.REACT_APP_API + `/cars/${id}`, {
        ...formData,
        imageUrl,
        galleryImages,
        youtubeUrl,
        showInFlipcard,
        updatedAt: Date.now(),
      });

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Car updated successfully",
        life: 3000,
      });

      navigate(
        formData.country === "Lebanon" ? "/lebanon-cars" : "/abidjan-cars"
      );
    } catch (error) {
      console.error("Error updating car:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update car",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-car-container">
      <Toast ref={toast} />
      <Card title="Edit Car" className="edit-car-card">
        <form onSubmit={handleSubmit} className="p-fluid">
          <div className="p-field mb-3">
            <label htmlFor="title">Title</label>
            <InputText
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              required
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="model">Model</label>
            <InputText
              id="model"
              value={formData.model}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, model: e.target.value }))
              }
              required
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="year">Year</label>
            <InputNumber
              id="year"
              value={formData.year}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  year: e.value || new Date().getFullYear(),
                }))
              }
              required
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="mileage">Mileage (km)</label>
            <InputNumber
              id="mileage"
              value={formData.mileage}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, mileage: e.value || 0 }))
              }
              required
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="price">Price ($)</label>
            <InputNumber
              id="price"
              value={formData.price}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, price: e.value || 0 }))
              }
              mode="currency"
              currency="USD"
              required
            />
          </div>

          <div className="p-field mb-3">
            <label htmlFor="description">Description</label>
            <InputTextarea
              id="text"
              value={formData.text}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, text: e.target.value }))
              }
              rows={5}
              required
              className="whitespace-pre-wrap w-full"
            />
          </div>

          <div className="p-field mb-3">
            <label>Main Image</label>
            <div className="p-inputgroup">
              <Button
                type="button"
                icon="pi pi-image"
                onClick={() => fileInputRef.current?.click()}
                loading={uploading}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleMainImageUpload}
                accept="image/*"
              />
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Preview"
                style={{ maxWidth: "200px", marginTop: "10px" }}
              />
            )}
          </div>

          <div className="p-field mb-3">
            <label>Gallery Images</label>
            <div className="p-inputgroup">
              <Button
                type="button"
                icon="pi pi-images"
                onClick={() => galleryInputRef.current?.click()}
                loading={uploading}
              />
              <input
                type="file"
                ref={galleryInputRef}
                style={{ display: "none" }}
                onChange={handleGalleryUpload}
                accept="image/*"
                multiple
              />
            </div>
            {galleryImages.length > 0 && (
              <Gallery
                images={galleryImages}
                isEditing={true}
                onImageUpload={handleGalleryUpload}
                onImageRemove={handleRemoveGalleryImage}
              />
            )}
          </div>

          <div className="p-field mb-3">
            <label htmlFor="youtubeUrl">YouTube Video URL (Optional)</label>
            <InputText
              id="youtubeUrl"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              className="w-full"
            />
          </div>

          <div
            className="p-field-checkbox"
            style={{ marginTop: "1rem", marginBottom: "1rem" }}
          >
            <Checkbox
              inputId="showInFlipcard"
              checked={showInFlipcard}
              onChange={(e) => setShowInFlipcard(e.target.checked ?? false)}
            />
            <label
              htmlFor="showInFlipcard"
              className="p-checkbox-label"
              style={{ marginLeft: "0.5rem" }}
            >
              Show in Home Flipcard Section
            </label>
          </div>

          <div className="form-section">
            <h3>Location Details</h3>
            <div className="field">
              <label htmlFor="country">Country</label>
              <div className="radio-group">
                <div className="radio-item">
                  <RadioButton
                    inputId="Lebanon"
                    name="country"
                    value="Lebanon"
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, country: e.value }))
                    }
                    checked={formData.country === "Lebanon"}
                  />
                  <label htmlFor="Lebanon" className="radio-label">
                    Lebanon
                  </label>
                </div>
                <div className="radio-item">
                  <RadioButton
                    inputId="Abidjan"
                    name="country"
                    value="Abidjan"
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, country: e.value }))
                    }
                    checked={formData.country === "Abidjan"}
                  />
                  <label htmlFor="Abidjan" className="radio-label">
                    Abidjan
                  </label>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-field mb-3">
              <small className="p-error">{error}</small>
            </div>
          )}

          <Button
            type="submit"
            label="Update Car"
            className="p-button-primary"
            loading={loading}
          />
        </form>
      </Card>
    </div>
  );
};

export default EditCar;
